/**
 * Form Submission Loader Component
 * Shows a loading spinner when a form is submitted to provide user feedback during long requests.
 * Works with forms that have the data-component="form-submission-loader" attribute.
 *
 * Features:
 * - Shows loading spinner overlay on form submit
 * - Disables submit buttons to prevent double-submission
 * - Adds loading state class to form for styling
 */

export function initFormSubmissionLoader(): void {
  const forms = document.querySelectorAll<HTMLFormElement>('[data-component="form-submission-loader"]');

  forms.forEach(form => {
    const spinner = form.querySelector<HTMLElement>('[data-component="loading-spinner"]');
    const submitButtons = form.querySelectorAll<HTMLButtonElement>('button[type="submit"]');

    if (!spinner) {
      console.warn('Form submission loader: No spinner element found in form');
      return;
    }

    const removeLoadingState = () => {
      form.classList.remove('authentication-form--loading');
      spinner.setAttribute('hidden', '');

      submitButtons.forEach(button => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
    };

    form.addEventListener('submit', () => {
      // Show loading state
      form.classList.add('authentication-form--loading');
      spinner.removeAttribute('hidden');

      // Disable all submit buttons to prevent double submission
      submitButtons.forEach(button => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });

      // Poll for error elements and restore form state if found
      const pollInterval = 100;
      const maxPollTime = 10000;
      let elapsedTime = 0;

      const errorCheckInterval = setInterval(() => {
        elapsedTime += pollInterval;

        const hasError =
          form.querySelector('.authentication-form__error') || form.querySelector('.field-validation-error');

        if (hasError || elapsedTime >= maxPollTime) {
          clearInterval(errorCheckInterval);

          if (hasError) {
            removeLoadingState();
          }
        }
      }, pollInterval);
    });
  });
}

// Auto-initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFormSubmissionLoader);
} else {
  initFormSubmissionLoader();
}
